import {RebuildingScene} from "../RebuildingScene";
import {anchorDepthFromTop, balancedMountAnchors, faceDirection, faceMountAnchor, faceOuterEnds, flippedDirection, getProfileObj, getSwingSide, linkProfileAndDoor, openingToFromLabel, pinDirection, profilesShareYOrientation, TProfile} from "./Helpers";
import {DoorTopDown} from "../../shared-2d/DoorTopDown";
import {PanelTopDown} from "../../shared-2d/PanelTopDown";
import {cellFill, flushGlazedFace, GlazeSide, plainPanelFill} from "../../shared-2d/CellFill";
import {FrmSceneLayout} from "../../drawfrm/FrmSceneLayout";
import {PairDoorGap} from "../../shared-2d/PairDoorGap";
import {ElevationApp} from "../../ElevationApp";
import {AddFx, ChildView} from "../../shared-2d/ChildView";
import {DrawFrmThing} from "../../drawfrm/DrawFrmThing";
import {DrawFrmTarget} from "../../drawfrm/DrawFrmTarget";
import {NavButtons} from "../../shared-2d/NavButtons";
import {doorTopLevelRow, isDoorCell, stickTurnRad} from "../frameGrid/BlockOps";
import {AngleMarker} from "../../shared-2d/AngleMarker";
import {MullionFaceExtension} from "../../shared-2d/MullionFaceExtension";
import {stickThicknessVar} from "../frameGrid/Util";
import {itemProfile, MdFrameBlock, MdFrameBlockChild, MdFrameStick} from "../../model/frame/MdFrameBlock";
import {MdFrameProfileType, MdFrameSpec, primaryDoorsItem} from "../../model/frame/MdFrameSpec";
import {FrmVar} from "../../drawfrm/FrmVar";
import {FrmRefPoint} from "../../drawfrm/FrmRefPoint";
import {inches} from "../../const";

type SwingInfo = {side: "left" | "right"; direction: "in" | "out" | "both"};
type TopDownOpts = {multiview?: boolean; hideLabels?: boolean};

// one piece the run pins between the profiles on either side of its cell: the outer-edge
// corner anchors to link, and the rabbet it glazes into. `glazeAt` is null for a door —
// the run balances a door's two rabbets instead, to keep the leaf square.
type RunMount = {
    left: {topLeft: any; bottomLeft: any};
    right: {topRight: any; bottomRight: any};
    glazeAt: GlazeSide | null;
};

// one built cell in the frame-grid run: its drawable(s), the pieces the run mounts (a
// door is one, a double-glazed lite is one per pane), and the swing direction at each
// edge (they differ for a pair whose two leaves swing opposite ways). isDoor drives the
// profile clearance gaps.
type RunCell = {
    draws: DrawFrmThing[];
    mounts: RunMount[];
    leftDirection: "in" | "out" | "both";
    rightDirection: "in" | "out" | "both";
    isDoor: boolean;
};

// one divider face in the run: its profile, and whether it was drawn flipped from the
// orientation its own invertY convention gives the run's swing (see faceFlipY). A
// flipped profile pins its cell edge with the opposite direction.
type RunProfile = {profile: TProfile; flipped: boolean};

// one element of the run, held in a single left-to-right ordered array: outer jamb,
// cell, double-faced mullion, cell, ..., outer jamb. Cells carry their built RunCell;
// dividers (jambs and mullions) carry only their drawables, since the run pins each
// cell to the profiles on either side of it as it is built.
type RunPart = {
    cell: RunCell | null;
    draws: DrawFrmThing[];
};

// the stick used for an interior divider the model has no stick child for.
const syntheticMullion: MdFrameStick = {id: "synthetic-mullion"};

// the radius of the angle arc drawn at a turn of the run, as a multiple of the jamb depth.
const ANGLE_ARC_RADIUS = 1.25;

// a turn smaller than this (radians) builds the mullion straight: the outside faces
// would meet too far away to draw.
const MIN_TURN_RAD = 1e-6;

type Pt = {x: number; y: number};

export class TopDownView extends ChildView {
    leftProfile!: TProfile;
    rightProfile!: TProfile;
    activeDoor!: DoorTopDown;
    inactiveDoor: DoorTopDown | null = null;
    pairDoorGap: PairDoorGap | null = null;
    topLabel!: DrawFrmThing;
    bottomLabel!: DrawFrmThing;
    target: DrawFrmTarget | null = null;
    target2: DrawFrmTarget | null = null;

    // when the full frame-grid run is drawn (single door in the top-level row with
    // side sections), all its drawables live here for the layout group, and getLayout
    // switches to them instead of the fixed left/door/right trio.
    private runItems: DrawFrmThing[] = [];
    private usesRun = false;

    // the direction of the run where it is being built, in radians off +x (clockwise
    // positive): 0 until an angled mullion turns it (see MdFrameStick.angle). Every
    // drawable built from then on is rotated by it before it is linked.
    private runRotation = 0;

    // omit the top/bottom location labels from the layout (and hide them). Used by
    // the export, which has its own name label.
    private hideLabels = false;

    constructor(elev: ElevationApp, add: AddFx, opts?: TopDownOpts) {
        super(elev, add);

        this.hideLabels = !!opts?.hideLabels;

        const activeSwingInfo = getSwingSide(this.elev, true);

        // the full horizontal run (side lites/panels + mullions) is drawn whenever the
        // door sits directly in the top-level row and has side sections — single doors and
        // pairs alike (the run expands a pair into its two leaves + gap). A door with no
        // side sections renders the same either way, so it stays on the simple path, as
        // does a door nested under a transom (doorTopLevelRow returns null there).
        const row = doorTopLevelRow(this.elev.frameSpec);
        const hasSideSections = !!row && row.children.filter(c => c.kind !== "stick").length > 1;

        if(row && hasSideSections) {
            this.buildRun(row, activeSwingInfo, opts);
        } else {
            this.buildSimple(activeSwingInfo, opts);
        }

        if(this.hideLabels) {
            this.topLabel.graphic.visible = false;
            this.bottomLabel.graphic.visible = false;
        }
    }

    // the original jamb / door(s) / jamb section. Handles single doors and pairs.
    private buildSimple(activeSwingInfo: SwingInfo, opts?: TopDownOpts) {
        // profile cross-section dims threaded into every getProfileObj below, so the
        // profile drawings read these instead of reaching into MdFrameSpec directly.
        const profileDims = {
            detail: this.elev.frameSpec.doorFrameProfile,
            jambDepth: this.elev.frameSpec.jambDepth,
            jambFace: this.elev.frameSpec.jambFace,
        };

        if(this.elev.frameSpec.isPair) {
            const inactiveSwingInfo = getSwingSide(this.elev, false);
            const leftDirection = activeSwingInfo.side === "left" ? activeSwingInfo.direction : inactiveSwingInfo.direction;
            const rightDirection = activeSwingInfo.side === "right" ? activeSwingInfo.direction : inactiveSwingInfo.direction;

            this.leftProfile = getProfileObj({
                elev: this.elev,
                view: this,
                ...profileDims,
                side: "left",
                direction: leftDirection,
            });

            this.rightProfile = getProfileObj({
                elev: this.elev,
                view: this,
                ...profileDims,
                side: "right",
                direction: rightDirection,
            });

        } else {

            this.leftProfile = getProfileObj({
                elev: this.elev,
                view: this,
                ...profileDims,
                side: "left",
                direction: activeSwingInfo.direction,
            });

            this.rightProfile = getProfileObj({
                elev: this.elev,
                view: this,
                ...profileDims,
                side: "right",
                direction: activeSwingInfo.direction,
            });
        }


        this.topLabel = openingToFromLabel(this.elev, "two", opts?.multiview);
        this.add(this.topLabel);

        // add door first so profile (and dimensions) is on top
        this.activeDoor = new DoorTopDown(this.elev, true);
        this.add(this.activeDoor);

        if(this.elev.frameSpec.isPair) {
            this.inactiveDoor = new DoorTopDown(this.elev, false);
            this.add(this.inactiveDoor);
        }

        this.add(this.leftProfile);
        this.add(this.rightProfile);

        if(this.elev.frameSpec.isPair) {
            linkProfileAndDoor({
                elev: this.elev,
                door: this.activeDoor,
                profile: activeSwingInfo.side === "left" ? this.leftProfile : this.rightProfile,
                side: activeSwingInfo.side,
                direction: activeSwingInfo.direction,
            });

            this.pairDoorGap = new PairDoorGap(this.elev, this);
            this.add(this.pairDoorGap)

            if(activeSwingInfo.side === "right") {
                this.pairDoorGap.right.linkTo(this.activeDoor.topLeft, 0, 0);
                this.pairDoorGap.left.linkTo(this.inactiveDoor!.topRight, 0, 0);
            } else {
                this.pairDoorGap.left.linkTo(this.activeDoor.topRight, 0, 0);
                this.pairDoorGap.right.linkTo(this.inactiveDoor!.topLeft, 0, 0);
            }

            const inactiveSwingInfo = getSwingSide(this.elev, false);
            linkProfileAndDoor({
                elev: this.elev,
                door: this.inactiveDoor!,
                profile: activeSwingInfo.side === "left" ? this.rightProfile : this.leftProfile,
                side: inactiveSwingInfo.side,
                direction: inactiveSwingInfo.direction,
            });

        } else {
            linkProfileAndDoor({
                elev: this.elev,
                door: this.activeDoor,
                profile: this.leftProfile,
                side: "left",
                direction: activeSwingInfo.direction,
            });

            linkProfileAndDoor({
                elev: this.elev,
                door: this.activeDoor,
                profile: this.rightProfile,
                side: "right",
                direction: activeSwingInfo.direction,
            });
        }

        this.bottomLabel = openingToFromLabel(this.elev, "one", opts?.multiview);
        this.add(this.bottomLabel);

        this.wireDimensionMode(opts);
    }

    // the full frame-grid horizontal run: outer jambs, one cell per grid cell (door
    // swing, lite in blue, fixed panel in grey), and a clamshell mullion for every
    // interior stick, all linked left-to-right. Handles single doors and pairs — a pair
    // door cell expands into its two leaves + gap (see buildPairRunCell). Cell widths come
    // from the last-solved model FrmVars.
    private buildRun(row: MdFrameBlock, swingInfo: SwingInfo, opts?: TopDownOpts) {
        this.usesRun = true;

        const spec = this.elev.frameSpec;
        const dir = swingInfo.direction;
        const profileDims = {
            detail: spec.doorFrameProfile,
            jambDepth: spec.jambDepth,
            jambFace: spec.jambFace,
        };

        // walk the row children into the run, in physical order. Every cell (item or
        // nested block) is preceded by a divider: the stick child just walked, else the
        // row's perimeter jamb at the left end, else a synthetic interior mullion.
        const parts: RunPart[] = [];
        let pendingStick: MdFrameStick | undefined = undefined;
        let prevCell: MdFrameBlockChild | null = null;

        // the cell the run has passed but not yet pinned, with the profile already
        // facing its left edge. The divider built after it supplies the right one.
        let openCell: {cell: RunCell; leftProfile: RunProfile} | null = null;
        const closeCell = (rightProfile: RunProfile) => {
            const open = openCell;
            openCell = null;
            if(open) this.linkRunCell(open.cell, open.leftProfile, rightProfile);
        };

        for(const child of row.children) {
            if(child.kind === "stick") {
                pendingStick = child.stick;
                continue;
            }

            // the divider before this cell, and the face it turns toward the cell.
            let leftProfile: RunProfile;

            if(prevCell === null) {
                const jamb = this.buildEdgeJamb("left", pendingStick, child, dir, profileDims);
                parts.push({cell: null, draws: [jamb.profile]});
                leftProfile = jamb;
            } else {
                const mullion = this.buildMullion(pendingStick ?? syntheticMullion, prevCell, child, dir, profileDims);
                parts.push({cell: null, draws: [mullion.leftHalf.profile, mullion.rightHalf.profile, ...mullion.extras]});

                // the mullion's left half faces the cell behind it, its right half the
                // cell about to be built.
                closeCell(mullion.leftHalf);
                leftProfile = mullion.rightHalf;
            }

            const cell = this.buildRunCell(child, swingInfo);
            parts.push({cell: cell, draws: cell.draws});
            openCell = {cell: cell, leftProfile: leftProfile};

            prevCell = child;
            pendingStick = undefined;
        }

        const rightJamb = this.buildEdgeJamb("right", pendingStick, prevCell, dir, profileDims);
        parts.push({cell: null, draws: [rightJamb.profile]});
        closeCell(rightJamb);

        this.topLabel = openingToFromLabel(this.elev, "two", opts?.multiview);
        this.add(this.topLabel);

        // add cells first so the profiles (and their dimensions) draw over them.
        const draw = (part: RunPart) => part.draws.forEach(d => { this.add(d); this.runItems.push(d); });
        parts.filter(part => part.cell).forEach(draw);
        parts.filter(part => !part.cell).forEach(draw);

        this.bottomLabel = openingToFromLabel(this.elev, "one", opts?.multiview);
        this.add(this.bottomLabel);

        this.wireDimensionMode(opts);
    }

    // pin one cell's mounted pieces to the profiles flanking it. Doors sit off the stop by
    // the frame gaps; lites/panels glaze straight into the rabbet (zero gaps). A pair
    // cell's two edges are its outer leaves, each linked with that leaf's own swing; a
    // double-glazed cell links a pane into each rabbet.
    private linkRunCell(cell: RunCell, leftProfile: RunProfile, rightProfile: RunProfile) {
        const gaps = cell.isDoor ? undefined : {jamb: 0, stop: 0};

        for(const mount of cell.mounts) {
            // a glazed piece takes the rabbet its glazing names on each side, and the
            // direction that rabbet pins with; a door takes the pair of rabbets that
            // keeps it square — matching mounting depths, faces coplanar — pinned by
            // its swing.
            const left = mount.glazeAt ? faceMountAnchor(leftProfile.profile, mount.glazeAt) : null;
            const right = mount.glazeAt ? faceMountAnchor(rightProfile.profile, mount.glazeAt) : null;
            const balanced = left && right ? null : balancedMountAnchors(leftProfile.profile, rightProfile.profile);

            linkProfileAndDoor({
                elev: this.elev, door: mount.left,
                profile: leftProfile.profile,
                side: "left",
                direction: left?.direction ?? swingPinDirection(leftProfile, cell.leftDirection),
                gaps,
                anchor: left?.anchor ?? balanced!.left,
            });
            linkProfileAndDoor({
                elev: this.elev, door: mount.right, profile: rightProfile.profile, side: "right",
                direction: right?.direction ?? swingPinDirection(rightProfile, cell.rightDirection),
                gaps, anchor: right?.anchor ?? balanced!.right,
            });
        }
    }

    // whether to draw one divider face flipped from the orientation its invertY
    // convention gives the run's swing. A flush-glazed cell has one rabbet, so its
    // profile turns to open on the glazed face; every other face keeps the run's
    // shared orientation — a profile from the other invertY group flips to line up
    // with the frame's own.
    private faceFlipY(type: MdFrameProfileType, facing: MdFrameBlockChild | null, dir: "in" | "out" | "both"): boolean {
        const flush = facing?.kind === "item" ? flushGlazedFace(facing.item) : null;
        if(flush) return pinDirection(dir) !== faceDirection(flush);
        return !profilesShareYOrientation(this.elev.frameSpec.profile, type);
    }

    // the outer jamb at one end of the run. Its inner face — the left jamb faces right,
    // the right jamb faces left — carries the stick's profile override.
    private buildEdgeJamb(
        side: "left" | "right",
        stick: MdFrameStick | undefined,
        facing: MdFrameBlockChild | null,
        dir: "in" | "out" | "both",
        profileDims: {detail: any; jambDepth: FrmVar; jambFace: FrmVar},
    ): RunProfile {
        const type = (side === "left" ? stick?.profileRight : stick?.profileLeft) ?? this.elev.frameSpec.profile;
        const flipY = this.faceFlipY(type, facing, dir);
        const profile = this.turned(getProfileObj({
            elev: this.elev, view: this, ...profileDims, side, direction: dir, profile: type, flipY,
        }));

        if(side === "left") this.leftProfile = profile;
        else this.rightProfile = profile;

        return {profile, flipped: flipY};
    }

    // build the drawable(s) for one run cell, plus the mounts and per-edge swing the run
    // linking needs. A pair door expands into two leaves + gap (buildPairRunCell); a
    // single door is one drawable mounted on the balanced rabbets; a lite/panel is one
    // pane drawable per rabbet its glazing calls for (two when double glazed).
    private buildRunCell(child: MdFrameBlockChild, swingInfo: SwingInfo): RunCell {
        const spec = this.elev.frameSpec;
        if(isDoorChild(spec, child) && spec.isPair) {
            return this.buildPairRunCell();
        }

        const common = {
            leftDirection: swingInfo.direction,
            rightDirection: swingInfo.direction,
            isDoor: isDoorChild(spec, child) || isSecondaryDoorChild(spec, child),
        };

        // a secondary door slot: another opening's door fills it, so show only its opening.
        const secondaryWidth = secondaryDoorWidth(spec, child);
        if(secondaryWidth) {
            const gap = this.turned(new PanelTopDown(this.elev, "metal", secondaryWidth, {outlineOnly: true}));
            return {draws: [gap], mounts: [mountOf(gap, null)], ...common};
        }

        if(isDoorChild(spec, child)) {
            const door = this.turned(new DoorTopDown(this.elev, true));
            return {draws: [door], mounts: [mountOf(door, null)], ...common};
        }

        const fill = child.kind === "item" ? cellFill(child.item) : plainPanelFill;
        const size = this.cellSize(child);
        const panes = fill.panes.map(side => {
            const draw = this.turned(new PanelTopDown(this.elev, fill.material, size));
            return {draw, mount: mountOf(draw, side)};
        });

        return {draws: panes.map(p => p.draw), mounts: panes.map(p => p.mount), ...common};
    }

    // the (solved) width a cell drawable spans in the section.
    private cellSize(child: MdFrameBlockChild): FrmVar {
        if(child.kind === "block") return child.block.size;
        if(child.kind === "item" && child.item.kind !== "doors" && child.item.size) return child.item.size;
        if(child.kind === "item") return new FrmVar(child.item.id + ":size:fallback", 12 * inches);
        // sticks are filtered out before the run cells are built — unreachable.
        throw new Error("cellSize: unexpected stick child");
    }

    // the pair door as a run cell: active + inactive leaves with the door gap between them,
    // each leaf swinging its own way. The gap links the two inner edges (mirrors buildSimple);
    // the run links the outer edges (the physically left/right-most leaf) to the neighbouring
    // profiles.
    private buildPairRunCell(): RunCell {
        const activeSwing = getSwingSide(this.elev, true);
        const inactiveSwing = getSwingSide(this.elev, false);

        this.activeDoor = this.turned(new DoorTopDown(this.elev, true));
        this.inactiveDoor = this.turned(new DoorTopDown(this.elev, false));
        this.pairDoorGap = this.turned(new PairDoorGap(this.elev, this));

        const activeOnLeft = activeSwing.side === "left";
        const leftLeaf = activeOnLeft ? this.activeDoor : this.inactiveDoor;
        const rightLeaf = activeOnLeft ? this.inactiveDoor : this.activeDoor;

        this.pairDoorGap.left.linkTo(leftLeaf.topRight, 0, 0);
        this.pairDoorGap.right.linkTo(rightLeaf.topLeft, 0, 0);

        return {
            draws: [this.activeDoor, this.inactiveDoor, this.pairDoorGap],
            mounts: [{
                left: {topLeft: leftLeaf.topLeft, bottomLeft: leftLeaf.bottomLeft},
                right: {topRight: rightLeaf.topRight, bottomRight: rightLeaf.bottomRight},
                glazeAt: null,
            }],
            leftDirection: activeOnLeft ? activeSwing.direction : inactiveSwing.direction,
            rightDirection: activeOnLeft ? inactiveSwing.direction : activeSwing.direction,
            isDoor: true,
        };
    }

    // rotate a drawable to the run's current direction. Done before it is linked, so
    // the link lands its anchor with the rotation applied.
    private turned<T extends DrawFrmThing>(draw: T): T {
        draw.graphic.rotation = this.runRotation;
        return draw;
    }

    // a clamshell mullion for one interior stick: a profile half facing each
    // neighbouring cell (each using that cell's own profile), offset apart by the
    // stick thickness. Mirrors FrameProfileScene.buildDoubleSidedMullion — the
    // left-facing half is primary (draws the capping faces), the right-facing half is
    // secondary (hides them). An angled stick turns the run here: the right half, and
    // everything built after it, is rotated. The two halves then hinge at their inside
    // faces (joinAngledHalves), both halves draw their faces, and an arc marks the
    // angle on the side it is measured on.
    private buildMullion(
        stick: MdFrameStick,
        leftCell: MdFrameBlockChild,
        rightCell: MdFrameBlockChild,
        dir: "in" | "out" | "both",
        profileDims: {detail: any; jambDepth: FrmVar; jambFace: FrmVar},
    ): {leftHalf: RunProfile; rightHalf: RunProfile; extras: DrawFrmThing[]} {
        const spec = this.elev.frameSpec;
        // the stick's per-face override wins; otherwise each half follows the cell it faces.
        const leftType = stick.profileLeft ?? childProfile(spec, leftCell);
        const rightType = stick.profileRight ?? childProfile(spec, rightCell);
        const leftFlipY = this.faceFlipY(leftType, leftCell, dir);
        const rightFlipY = this.faceFlipY(rightType, rightCell, dir);

        // an air gap has no stock: its halves only place the cells a gap apart (below).
        const gap = !!stick.airGap;

        // left half faces the left cell (rabbet opens left) → a "right" jamb; right
        // half faces the right cell → a "left" jamb.
        const leftHalf = this.turned(getProfileObj({
            elev: this.elev, view: this, ...profileDims,
            side: "right", direction: dir, profile: leftType, clamshell: true,
            hideFaces: gap,
            hideFaceDims: gap,
            flipY: leftFlipY,
        }));

        // the run turns at an angled stick: the right half and all that follows it.
        const turn = stickTurnRad(stick);
        const angled = Math.abs(turn) > MIN_TURN_RAD;
        this.runRotation += turn;

        const rightHalf = this.turned(getProfileObj({
            elev: this.elev, view: this, ...profileDims,
            side: "left", direction: dir, profile: rightType, clamshell: true,
            hideFaces: !angled || gap,
            hideFaceDims: angled || gap,
            flipY: rightFlipY,
        }));

        if(gap) {
            leftHalf.graphic.visible = false;
            rightHalf.graphic.visible = false;
        }

        const jambDepth = profileDims.jambDepth.get();
        const extras: DrawFrmThing[] = [];

        // where the angle marker sits: the vertex of the angle on the side it is
        // measured on, relative to `ref`.
        let markerAt: {ref: FrmRefPoint; at: Pt};

        if(angled) {
            const joint = this.joinAngledHalves(leftHalf, rightHalf, turn, jambDepth);
            extras.push(joint.extension);
            markerAt = {ref: leftHalf.faceAnchor, at: joint.vertex};
        } else {
            // join the halves at their rabbets — where the neighbouring cells attach, so
            // the link stays robust with cells present — a stick thickness apart.
            // Offsetting in depth by the two profiles' rabbet-from-top-face difference
            // lands their outer faces at the same depth whichever way each half is
            // drawn, so differing profiles read as face-aligned while each cell's glazing
            // sits at its own profile's depth. Equal profiles drawn alike give a zero
            // depth offset, i.e. the plain rabbets-a-thickness-apart join.
            const thickness = stickThicknessVar(stick, spec).get();
            const depthL = anchorDepthFromTop(leftHalf, jambDepth);
            const depthR = anchorDepthFromTop(rightHalf, jambDepth);
            rightHalf.primaryDoorAnchor.linkTo(leftHalf.primaryDoorAnchor, thickness, depthR - depthL);

            // the mullion's centre: half a thickness in from the rabbet, at mid depth.
            markerAt = {ref: leftHalf.primaryDoorAnchor, at: {x: thickness / 2, y: jambDepth / 2 - depthL}};
        }

        if(stick.angle) {
            const marker = new AngleMarker({deg: stick.angle.get(), turnRad: turn, radius: ANGLE_ARC_RADIUS * jambDepth});
            marker.graphic.rotation = this.runRotation - turn;
            marker.levelText();
            marker.anchor.linkTo(markerAt.ref, markerAt.at.x, markerAt.at.y);
            extras.push(marker);
        }

        return {
            leftHalf: {profile: leftHalf, flipped: leftFlipY},
            rightHalf: {profile: rightHalf, flipped: rightFlipY},
            extras,
        };
    }

    // join the two halves of an angled mullion. On the inside of the angle (the side
    // the run bends toward) each half keeps its full face, and the two faces meet end
    // to end at the hinge: the right half pivots there. On the outside the faces run on
    // past the hinge until they meet in a point; the extension draws those two runs.
    // Returns the extension and the angle's vertex on the measured (locationOne,
    // bottom) side — the hinge when the run bends that way, else the meeting point.
    // Coordinates are in the left half's frame, relative to its faceAnchor.
    private joinAngledHalves(left: TProfile, right: TProfile, turn: number, jambDepth: number): {extension: MullionFaceExtension; vertex: Pt} {
        // a positive turn bends toward the bottom of the section (see stickTurnRad).
        const inside = turn > 0 ? "bottom" : "top";
        const outside = turn > 0 ? "top" : "bottom";

        const cos = Math.cos(turn);
        const sin = Math.sin(turn);
        const rotate = (p: Pt): Pt => ({x: cos * p.x - sin * p.y, y: sin * p.x + cos * p.y});
        const relativeTo = (anchor: FrmRefPoint, p: Pt): Pt => ({x: p.x - anchor.getLocalX(), y: p.y - anchor.getLocalY()});

        const endsL = faceOuterEnds(left, jambDepth);
        const endsR = faceOuterEnds(right, jambDepth);

        // land the right half's inside face end on the left half's: the hinge.
        const hinge = relativeTo(left.faceAnchor, endsL[inside]);
        const hingeR = rotate(relativeTo(right.faceAnchor, endsR[inside]));
        const offset = {x: hinge.x - hingeR.x, y: hinge.y - hingeR.y};
        right.faceAnchor.linkTo(left.faceAnchor, offset.x, offset.y);

        // the left half's outside face runs along +x; the right half's along the turned
        // run direction from its own face end. Meet them.
        const outL = relativeTo(left.faceAnchor, endsL[outside]);
        const outR0 = rotate(relativeTo(right.faceAnchor, endsR[outside]));
        const outR = {x: offset.x + outR0.x, y: offset.y + outR0.y};
        const along = (outL.y - outR.y) / sin;
        const meet = {x: outR.x + along * cos, y: outL.y};

        const extension = new MullionFaceExtension([outL, meet, outR]);
        extension.graphic.rotation = this.runRotation - turn;
        extension.anchor.linkTo(left.faceAnchor, 0, 0);

        return {extension, vertex: turn > 0 ? hinge : meet};
    }

    // dimensioning detail follows the global "Simple Dimensions" ribbon toggle; the
    // multiview (FrameAll) case keeps its own hidden dimensioning via hideDimensions().
    private wireDimensionMode(opts?: {multiview?: boolean}) {
        if(!opts?.multiview) {
            this.trackSubscription(this.elev.viewSettings.dimSimple.subscribeAndFireLast(simple => {
                this.dimensionMode.emit(simple ? "standard" : "extended");
            }));
        }
    }

    getLayout(navButton?: any) {
        if(navButton) this.add(navButton)

        const body = this.usesRun
            ? FrmSceneLayout.group(this.runItems).grow(1)
            : FrmSceneLayout.group([
                this.leftProfile,
                this.activeDoor,
                this.pairDoorGap && this.pairDoorGap,
                this.inactiveDoor && this.inactiveDoor,
                this.rightProfile,
                this.target,
                this.target2,
            ]).grow(1);

        return FrmSceneLayout.column(
            navButton ? FrmSceneLayout.absolute(navButton, {
                top: 0,
                right: 0,
            }) : null,
            this.hideLabels ? null : FrmSceneLayout.row(this.topLabel).justifyContent("center"),
            body,
            this.hideLabels ? null : FrmSceneLayout.row(this.bottomLabel).justifyContent("center"),
        );
    }

    hideDimensions() {
        this.dimensionMode.emit(false);
        return this;
    }
}

// the direction a cell edge pins to a divider face with, from the cell's swing.
function swingPinDirection(face: RunProfile, swing: "in" | "out" | "both"): "in" | "out" {
    return face.flipped ? flippedDirection(swing) : pinDirection(swing);
}

// the corner anchors a drawable presents to the run, as one mount.
function mountOf(draw: {topLeft: any; bottomLeft: any; topRight: any; bottomRight: any}, glazeAt: GlazeSide | null): RunMount {
    return {
        left: {topLeft: draw.topLeft, bottomLeft: draw.bottomLeft},
        right: {topRight: draw.topRight, bottomRight: draw.bottomRight},
        glazeAt,
    };
}

// whether a grid-cell child is the door: the door cell (doors over threshold) the primary
// opening fills.
function isDoorChild(spec: MdFrameSpec, child: MdFrameBlockChild): boolean {
    return child.kind === "block" && isDoorCell(child.block) && doorCellItem(child.block) === primaryDoorsItem(spec);
}

// whether a grid-cell child is a secondary door slot's door cell.
function isSecondaryDoorChild(spec: MdFrameSpec, child: MdFrameBlockChild): boolean {
    return child.kind === "block" && isDoorCell(child.block) && doorCellItem(child.block) !== primaryDoorsItem(spec);
}

// the design width of a secondary door slot's door cell, or null for any other child.
function secondaryDoorWidth(spec: MdFrameSpec, child: MdFrameBlockChild): FrmVar | null {
    if(!isSecondaryDoorChild(spec, child) || child.kind !== "block") return null;
    const doors = doorCellItem(child.block);
    return doors?.kind === "doors" ? doors.width : null;
}

// the doors item of a door cell.
function doorCellItem(block: MdFrameBlock) {
    const first = block.children.find(c => c.kind === "item" && c.item.kind === "doors");
    return first?.kind === "item" ? first.item : undefined;
}

// the profile type a mullion half should use for the cell it faces. A nested block
// has no single profile, so it follows the spec default.
function childProfile(spec: Parameters<typeof itemProfile>[0], child: MdFrameBlockChild): ReturnType<typeof itemProfile> {
    if(child.kind === "item") return itemProfile(spec, child.item);
    return spec.profile;
}

export class TopDownScene extends RebuildingScene {
    protected rebuildOnValueEdit(): boolean {
        return true;
    }

    protected build(): void {
        const view = new TopDownView(this.elev, input => this.add(input));

        this.setupLayout(
            view.getLayout(new NavButtons({
                expand: () => this.elev.navTo("frameProfile"),
                collapse: () => this.elev.navTo("frameAll"),
            }))
                .grow(1)
                .gap(20)
                .padding(20),
        );
    }
}
